import { useEffect, useRef, useState } from "react";
import { Link2, Check, AlertCircle } from "lucide-react";
import { copyTextToClipboard } from "../agent/clipboard.js";
import { ensureEmployerLink } from "../matchingRecommendation/matchingRecommendationApi.js";
import { buildEmployerResumeUrl } from "../public/publicResumeApi.js";

const RESET_MS = 4000;

/**
 * "Copy this worker's resume link" for one recommendation on tab 3.
 *
 * WHAT THE OPERATOR IS ACTUALLY DOING. This platform has no notification
 * sender - no employer inbox, no e-mail, no LINE integration - and this button
 * does not pretend otherwise. It hands the operator a URL to paste into
 * whatever channel they already use with that employer. Everything after the
 * paste happens outside this system, which is why the row keeps saying
 * "waiting" until the employer actually answers on the page itself.
 *
 * IDEMPOTENT BY DESIGN. The token comes from the server, which mints one only
 * if the row has none. Pressing this twice, next week, or as a different
 * operator yields the same URL - the first press may already be sitting in a
 * LINE chat, and a rotating link would break it.
 *
 * NEVER FAILS SILENTLY. The live backoffice is served over plain HTTP, where
 * navigator.clipboard.writeText can be blocked outright. copyTextToClipboard
 * falls back to execCommand, and when even that fails this shows the real URL
 * in a focused, selected, read-only input so the operator can copy it by hand.
 * Same treatment CopyInviteLinkButton already uses, for the same reason: an
 * operator who thinks they copied a link and pastes nothing has told an
 * employer nothing and will not find out for days.
 */
export default function CopyResumeLinkButton({ recommendationId, workerName, employerLinkToken = null }) {
  // "idle" | "working" | "copied" | "manual" | "error"
  const [state, setState] = useState("idle");
  const [url, setUrl] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const manualInputRef = useRef(null);
  const resetTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(resetTimerRef.current), []);

  useEffect(() => {
    if (state === "manual") {
      manualInputRef.current?.focus();
      manualInputRef.current?.select();
    }
  }, [state]);

  function scheduleReset() {
    clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => setState("idle"), RESET_MS);
  }

  async function handleClick(event) {
    event.stopPropagation();
    if (state === "working") {
      return;
    }

    clearTimeout(resetTimerRef.current);
    setState("working");
    setErrorMessage(null);

    // The list may already carry the token, in which case there is nothing to
    // ask the server for.
    let token = employerLinkToken;
    if (!token) {
      try {
        token = (await ensureEmployerLink(recommendationId))?.employerLinkToken ?? null;
      } catch (error) {
        setErrorMessage(resolveErrorMessage(error));
        setState("error");
        scheduleReset();
        return;
      }
    }

    const resumeUrl = buildEmployerResumeUrl(token);
    if (!resumeUrl) {
      // The server answered but withheld the token - a real outcome, not
      // something to paper over with a fake success.
      setErrorMessage("เซิร์ฟเวอร์ไม่ได้ส่งลิงก์กลับมา กรุณาลองใหม่อีกครั้ง");
      setState("error");
      scheduleReset();
      return;
    }

    setUrl(resumeUrl);
    const copied = await copyTextToClipboard(resumeUrl);
    setState(copied ? "copied" : "manual");
    if (copied) {
      scheduleReset();
    }
  }

  return (
    <>
      <button
        type="button"
        className={`wx-copy-invite ${stateModifier(state)}`.trim()}
        onClick={handleClick}
        disabled={state === "working"}
        aria-label={workerName ? `คัดลอกลิงก์ประวัติของ ${workerName}` : "คัดลอกลิงก์ประวัติ"}
      >
        {state === "copied" ? <Check size={14} aria-hidden="true" /> : null}
        {state === "error" ? <AlertCircle size={14} aria-hidden="true" /> : null}
        {state !== "copied" && state !== "error" ? <Link2 size={14} aria-hidden="true" /> : null}
        <span>{buttonText(state)}</span>
      </button>

      {state === "manual" && url ? (
        <div className="wx-copy-invite-manual" onClick={(event) => event.stopPropagation()}>
          <p className="wx-copy-invite-manual__note">คัดลอกอัตโนมัติไม่สำเร็จ กด Ctrl+C เพื่อคัดลอกลิงก์นี้</p>
          <input
            ref={manualInputRef}
            type="text"
            readOnly
            value={url}
            className="wx-copy-invite-manual__url"
            aria-label="ลิงก์ประวัติสำหรับนายจ้าง"
          />
        </div>
      ) : null}

      {state === "error" && errorMessage ? (
        <p className="wx-copy-invite-error" role="alert">
          {errorMessage}
        </p>
      ) : null}
    </>
  );
}

function stateModifier(state) {
  if (state === "copied") return "wx-copy-invite--copied";
  if (state === "error") return "wx-copy-invite--error";
  return "";
}

function buttonText(state) {
  switch (state) {
    case "working":
      return "กำลังเตรียมลิงก์...";
    case "copied":
      return "คัดลอกแล้ว";
    case "manual":
      return "คัดลอกเองด้านล่าง";
    case "error":
      return "ไม่สำเร็จ";
    default:
      return "คัดลอกลิงก์ประวัติ";
  }
}

/** Surfaces what the server actually said rather than replacing it. */
function resolveErrorMessage(error) {
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ออกลิงก์ของรายการนี้";
  }
  if (error?.status === 404) {
    return "ไม่พบรายการนี้ อาจถูกลบไปแล้ว";
  }
  return error?.body?.message || error?.message || "ไม่สามารถเตรียมลิงก์ได้ กรุณาลองใหม่อีกครั้ง";
}
